import React from 'react';
import {
  Drawer,
  List,
  Divider,
  useTheme,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  ListItemButton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { MENU_ITEMS, SECTIONS } from '../../constants/navigation';
import { useUserSession } from '../../contexts/UserContext';

const DRAWER_WIDTH = 200;
const COLLAPSED_DRAWER_WIDTH = 64;

const NavigationDrawer = ({ 
  isMobile,
  isDrawerOpen,
  setIsDrawerOpen,
  activeSection,
  setActiveSection,
  navigate
}) => {
  const theme = useTheme();
  const { userSession } = useUserSession();
  const [expandedMenu, setExpandedMenu] = React.useState('');

  const handleMenuClick = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? '' : menuId);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      sx={{
        width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100% - 64px)',
          background: 'rgba(255, 255, 255, 0.95)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          borderRight: 'none',
          '& .MuiList-root': { p: 0, width: '100%' },
          '& .MuiListItem-root, & .MuiListItemButton-root': {
            width: '100%',
            px: 0,
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
              width: COLLAPSED_DRAWER_WIDTH,
              justifyContent: 'center'
            }
          }
        }
      }}
      onMouseEnter={() => !isMobile && setIsDrawerOpen(true)}
      onMouseLeave={() => !isMobile && setIsDrawerOpen(false)}
    >
      <List>
        {SECTIONS.map((section) => (
          <Tooltip title={!isDrawerOpen ? section.label : ''} placement="right" key={section.id}>
            <ListItem
              button
              selected={activeSection === section.id}
              onClick={() => {
                setActiveSection(section.id);
                navigate(section.path);
              }}
              sx={{
                minHeight: 36,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }
              }}
            >
              <ListItemIcon>{section.icon}</ListItemIcon>
              {isDrawerOpen && (
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{
                    fontWeight: activeSection === section.id ? 700 : 400
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
        <Divider sx={{ my: 1 }} />
        {Object.values(MENU_ITEMS).map((menu) => (
          <React.Fragment key={menu.id}>
            <Tooltip title={!isDrawerOpen ? menu.label : ''} placement="right">
              <ListItemButton
                onClick={() => handleMenuClick(menu.id)}
                sx={{ minHeight: 36, px: 1.5 }}
              >
                <ListItemIcon sx={{ mr: isDrawerOpen ? 2 : 'auto' }}>
                  {menu.icon}
                </ListItemIcon>
                {isDrawerOpen && (
                  <>
                    <ListItemText primary={menu.label} />
                    {expandedMenu === menu.id ? <ExpandLess /> : <ExpandMore />}
                  </>
                )}
              </ListItemButton>
            </Tooltip>
            {(isDrawerOpen || (!isDrawerOpen && expandedMenu === menu.id)) && (
              <Collapse in={expandedMenu === menu.id} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {menu.items.map((item) => {
                    const r = item.roles || (item.role ? [item.role] : null);
                    const visible =
                      !r || r.some((role) => (userSession?.roles || []).includes(role));
                    if (!visible) return null;
                    return (
                      <Tooltip key={item.id} title={!isDrawerOpen ? item.label : ''} placement="right">
                        <ListItemButton
                          sx={{ pl: isDrawerOpen ? 3 : 1.5, minHeight: 36 }}
                          onClick={() => handleMenuItemClick(item.path)}
                        >
                          <ListItemIcon sx={{ mr: isDrawerOpen ? 2 : 'auto' }}>
                            {item.icon}
                          </ListItemIcon>
                          {isDrawerOpen && <ListItemText primary={item.label} />}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default NavigationDrawer; 